import axios, { AxiosError, AxiosStatic, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import Cookies from 'universal-cookie'
import { apiClient } from '../../services/client'
import { toastErrorDeduped } from '../../Utils/toastDedup'
import { isOwnedComponentRefusal, isOwnedNotRunnable } from '../../services/ownsErrors'
const ignoreErrorHandlingEndpointList = ['/auth/jwt/logout/',
  // The re-scan dialog renders every refusal itself: missing targets, a version
  // that no longer fits today's components, the inactive-components confirm, a
  // component the user may not use, and the orchestrator-outage 503.
  '/rescan-options/', '/rescan/',
  '/auth/jwt/refresh/', "/organizations/current/",
  // Patch sessions render their own errors: a chat may now hold several live
  // sessions, so starting a second one no longer 409s, but the 400 on a free
  // chat still has to disable the entry point — that does not survive a
  // generic toast. NOTE the missing trailing slash: the chat service serves
  // the bare `/chat/{id}/patch-session` for read and start, plus
  // `/check-connectivity` and `/end` beneath it, so a "/patch-session/" entry
  // would silently stop matching the first two.
  //
  // This also matches the caller-scoped listing `GET /chat/patch-sessions`,
  // which the old "/patch-session/" spelling did not. That is INTENDED, not
  // collateral: the listing backs a background badge poll on the chat list, so
  // a failure there is worth a missing badge, not a toast on every page load.
  // Do not "fix" the overmatch by re-adding a slash — that would re-enable
  // both toasts.
  // See docs/superpowers/specs/2026-08-09-chatbot-service-migration-contract.md §13, §14
  "/patch-session",
  // Approvals render on the approval card. 409 ("already decided") and 404
  // ("stale card") are non-events that must refetch silently rather than toast,
  // and the two 403s need two different messages — "waiting on the chat owner"
  // versus "you do not have permission to approve this action". A generic toast
  // can express none of that, and NestJS bodies ({statusCode, message, error})
  // render as "undefined" through the default branch below anyway.
  // See docs/superpowers/specs/2026-08-09-chatbot-service-migration-contract.md §12, §14
  "/approvals",
  // The chat stream's Centrifugo token. Both of its failure modes are already
  // rendered in place by `useChatStream`, and a toast is actively wrong on each.
  //
  // §14 calls a 503 here operator misconfiguration where "the answer still
  // arrives", and the hook degrades to polling `/events` for exactly that
  // reason — so the generic 5xx "Oops! Something went wrong" would contradict a
  // conversation that is working. Any other status already becomes the hook's
  // `error`, which renders as `<ChatError/>` beside the "Reconnecting…" line
  // the `disconnected` state raises, so the toast duplicates the first failure.
  //
  // And it would not stop at the first: centrifuge-js's `getToken` refreshes
  // this on a timer for as long as a chat is open — every ~50s now that §6 cut
  // `ttl_seconds` from 600 to 60, per open chat, per tab — so a persistent
  // failure toasts indefinitely, about something the user cannot act on.
  // See docs/superpowers/specs/2026-08-09-chatbot-service-migration-contract.md §6, §14
  "/stream-token",
  // DNS overrides render every error inline instead: the 409 conflict message
  // names the exact record blocking the create, and it has to appear on the
  // domain field where the user can act on it, not in a transient toast.
  // The card owns its 4xx AND 5xx rendering as a result.
  // See docs/superpowers/specs/2026-08-03-dns-overrides-design.md §4.1
  "/dns-overrides/",
  // Two-factor renders every failure in place: a wrong code keeps the visitor on
  // the screen with an inline error, `challenge_invalid` sends them back to the
  // password step, and a 429 says "too many attempts". A generic toast would say
  // the same thing worse — or say "temporarily unavailable" about a rate limit.
  "/auth/2fa/",
  // The superuser console renders every failure in place: a 403 is the "superusers
  // only" page, a refused operation (`{detail, code}`) is a banner with the code's
  // own wording, and a 400 is a message under its field. Each of them is lost in a
  // generic toast, and the 403 must not eject a reader from the page that explains it.
  "/platform/",
  // Compliance renders its own 403 in place. The page is reachable on its
  // feature flag alone — it has no `permissionKey`, so that an org whose roles
  // do not yet carry `api:compliance:view` can still find it — which makes a
  // 403 here the EXPECTED first response rather than an anomaly. The generic
  // toast would fire once per query on a page that opens four of them, saying
  // nothing the <NotPermission/> panel does not say better.
  "/compliance/",
  // Bulk asset import renders its own 400s. They are PER ITEM — a list of
  // `{index, seed, error}` objects, one per bad position in the pasted list —
  // and the dialog shows each message beside the value that caused it. A toast
  // can only flatten that into one line, and the user then has no way to tell
  // WHICH of fifty pasted hosts was rejected.
  // See docs/superpowers/specs/2026-09-26-multi-target-scans-design.md §4.5
  "/assets/import/",
  // Issues renders every error in place. Three reasons, and each on its own
  // would be enough:
  //  - The board is reachable on its feature flag alone (no `permissionKey`),
  //    so a 403 from a role without `api:issue` is the EXPECTED first response
  //    on a page that opens four queries at once.
  //  - `GET /issues/<id>/` and `/issues/by-number/<n>/` answer 404 for an id in
  //    another organization — the contract is explicit that it must not leak
  //    whether the resource exists elsewhere — and the panel says "no such
  //    issue" where a toast would say nothing useful.
  //  - The admin screen's refusals are INSTRUCTIONS ("this team still holds
  //    issues — route them elsewhere first"), which a flattened toast destroys.
  "/issues/",
  // Report name preview and tag autocomplete are best-effort and run while the
  // user types: a 400 from a half-filled form must leave the previous
  // placeholder alone, not toast on every keystroke.
  "/reports/default-name/",
  "/reports/tags/",
  // Component Settings renders its own failures (field errors, read-only on 403).
  '/component-configs']

/**
 * Report item routes — `PATCH|DELETE /reports/{id}/` and `POST|DELETE
 * /reports/{id}/pin/` — render their own failures: edit shows the server's
 * per-field 400s inside the dialog, delete explains a 409 ("still being
 * generated") in its own words, and a 403 is the product state "your role lacks
 * report: edit/delete" that must not eject the user to the dashboard.
 *
 * A pattern, not a substring: the id is a 26-character ULID, which keeps the
 * launch endpoints (`/reports/scan-report/`), `/reports/{id}/download/` and the
 * list on the generic handling.
 */
const reportItemRoute = /\/reports\/[0-9A-Za-z]{20,}\/(?:pin\/)?(?:\?.*)?$/

/**
 * Surfaces where a 403 is a documented PRODUCT STATE rather than an access
 * failure, so the forbidden redirect below must not fire.
 *
 * Suppressing the toast is not enough on its own: the 403 branch dispatches
 * `auth:forbidden`, which `src/app/App.tsx` handles by replacing the route with
 * `/org-dashboard`. A surface that renders its own 403 in place cannot do so
 * while it is being unmounted.
 *
 * The first two entries are owner-only endpoints on an otherwise org-shared
 * resource: any member of the org may open, read and send into a colleague's
 * chat, but every `/patch-session` endpoint and `POST /approvals` is restricted
 * to the chat's owner. So merely OPENING a colleague's chat fires
 * `GET /chat/{id}/patch-session` and gets a 403 — an entirely normal state that
 * must render as a read-only view ("waiting on the chat owner"), not eject the
 * user to the dashboard.
 *
 * `/stream-token` is here for a different reason, and it is the stronger one: it
 * is fetched by a BACKGROUND TIMER. centrifuge-js's `getToken` refreshes it for
 * as long as a chat stays open, and §6 cut `ttl_seconds` from 600 to 60, so that
 * is roughly every 50s — per open chat, per tab, indefinitely, about ten times
 * as often as under Django. A 403 from one of those refreshes (an org switch is
 * the plausible trigger: the request carries `X-Current-Organization`, and a
 * chat opened under the previous org is no longer the caller's) would navigate
 * the user to `/org-dashboard` from wherever they happen to be — mid-form,
 * mid-scan, on a page with nothing to do with chat. A periodic timer must never
 * be able to change the route. `useChatStream` renders the resulting dead socket
 * itself, as "Reconnecting…" and `<ChatError/>`.
 *
 * Deliberately a separate, narrower list from the toast exemptions above:
 * `/dns-overrides/` and the auth endpoints keep today's redirect behaviour.
 * See docs/superpowers/specs/2026-08-09-chatbot-service-migration-contract.md §6, §12, §14
 */
const ignoreForbiddenRedirectList = [
  // `COMPONENT_NOT_PERMITTED` on a re-scan is a 403 the dialog explains in place.
  '/rescan/',
  // `two_factor_disabled` is a 403 the screen words itself — not a reason to leave the page.
  '/auth/2fa/',
  // The console's own 403 is the "superusers only" page, rendered in place.
  '/platform/',
  '/patch-session',
  '/approvals',
  '/stream-token',
  // Compliance is reachable on its feature flag alone, so a user whose roles
  // lack `api:compliance:view` WILL land on it. Without this entry the first
  // query's 403 dispatches `auth:forbidden` and replaces the route with
  // `/org-dashboard` — the sidebar item would appear to do nothing but bounce.
  // The page renders <NotPermission/> in place instead, which tells the user
  // what is actually missing.
  '/compliance/',
  // Issues is reachable on its feature flag alone for the same reason, so a
  // user whose roles lack `api:issue` WILL land on the board. Without this
  // entry the first query's 403 dispatches `auth:forbidden` and replaces the
  // route with `/org-dashboard` — the sidebar item would appear to do nothing
  // but bounce. The page renders <NotPermission/> in place instead.
  //
  // It also covers the 503 the contract defines for an unreachable permission
  // service, which is NOT an auth failure and must never eject the user.
  '/issues/',
  // Edit/delete controls are hidden without their permission, but a role
  // backfill may lag the UI: the dialog says so itself instead of bouncing.
  '/reports/default-name/',
  '/reports/tags/',
  // A 403 on Save/Reset (no `edit`) is shown in place; the form stays readable.
  '/component-configs',
]

/**
 * Whether the global error toast should fire for a request URL.
 *
 * Substring match, deliberately: one entry covers a resource and everything
 * beneath it. Exported for the pin in `__tests__/errorHandlingEndpoints.test.ts`
 * — the matching is load-bearing and a path rename can silently re-enable the
 * toast on a surface that renders its own errors.
 */
export const isShouldErrorHandling = (url: string | undefined): boolean => {
  if (url) {
    return !ignoreErrorHandlingEndpointList.some((item) => url.includes(item)) && !reportItemRoute.test(url)
  } else {
    return true
  }
}

/**
 * Whether a 403 on this URL should redirect the user to the org dashboard.
 *
 * Exported for the pin in `__tests__/errorHandlingEndpoints.test.ts`, for the
 * same reason `isShouldErrorHandling` is: the matching is plain substring, so a
 * path rename silently re-enables a navigation on a surface that must not have
 * one — and here the consequence is a route change, not merely a toast.
 */
export const isShouldForbiddenRedirect = (url: string | undefined): boolean => {
  if (!url) return true
  return !ignoreForbiddenRedirectList.some((item) => url.includes(item)) && !reportItemRoute.test(url)
}

/**
 * Renders one entry of a DRF-style `{field: detail}` error body as text.
 *
 * Extracted and exported for a pin: the multi-target scan contract answers
 * `POST /scan/` with `{"seeds": [{"index": 0, "seed": "x", "error": "…"}]}`, and
 * the previous inline `detail[0]` interpolated that OBJECT straight into a
 * template string — the toast read `[object Object]`. A generic formatter has to
 * degrade to something a user can act on, since by definition it runs on bodies
 * this build has never seen.
 *
 * Surfaces that need the per-index detail (which chip to mark red) parse the raw
 * body themselves via `parseSeedsError`; this is only the last-resort toast.
 */
export const formatErrorDetail = (detail: unknown): string => {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map(formatErrorDetail).filter(Boolean).join(' · ')
  }
  if (detail && typeof detail === 'object') {
    const record = detail as Record<string, unknown>
    // The seeds/mixed row shapes: name the offending value, then the reason.
    const subject = typeof record.seed === 'string' ? record.seed : undefined
    const reason =
      typeof record.error === 'string'
        ? record.error
        : typeof record.type === 'string'
          ? record.type
          : undefined
    if (subject && reason) return `${subject}: ${reason}`
    if (subject || reason) return (subject || reason) as string
    return ''
  }
  return detail == null ? '' : String(detail)
}
// Track if a token refresh is in progress to prevent multiple refresh calls
let isRefreshing = false
// Queue of failed requests to retry after refresh succeeds
let failedRequestsQueue: Array<{
  resolve: (value?: any) => void
  reject: (error?: any) => void
}> = []

/**
 * Clear all client-side session state and notify React Router to navigate to
 * the login page. Called from every terminal authentication-failure path.
 */
async function performLogout() {
  const { useAuthStore } = await import('../../store/authStore')
  const { queryClient } = await import('../../lib/react-query/queryClient')
  queryClient.cancelQueries()
  queryClient.clear()
  useAuthStore.getState().Logout_(false)

  // Dispatch custom event to trigger navigation from React Router
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

/**
 * A bare axios instance with NO interceptors, used only to probe session
 * validity. Because it bypasses configureInterceptors it never re-enters the
 * response interceptor (no recursion) and its `validateStatus` is not
 * overwritten, so we can read any HTTP status directly.
 */
export const sessionProbeClient = axios.create({ withCredentials: true })

// De-dupe concurrent probes — e.g. the scan-details page long-polls and can
// fire many 403s at once the moment a session dies.
let sessionCheckInFlight: Promise<boolean> | null = null

/**
 * Whether the current session is still authenticated, determined by probing the
 * permission-free `/auth/users/me/` endpoint. Any non-2xx response means "not
 * authenticated" — there is no permission dimension on one's own profile — which
 * lets us tell an authentication 403 apart from a genuine permission 403 using a
 * stable HTTP-status signal, with no dependency on localized error messages or a
 * backend error-code field.
 *
 * Network/transport errors fail safe (treated as still-valid) so a flaky
 * connection never logs the user out.
 */
export function isSessionValid(): Promise<boolean> {
  if (!sessionCheckInFlight) {
    const apiEndpoint = window?.RUNTIME_CONFIG?.REACT_APP_API_ENDPOINT || process.env.REACT_APP_API_ENDPOINT
    sessionCheckInFlight = sessionProbeClient
      .get(`${apiEndpoint}/auth/users/me/`, { validateStatus: () => true })
      .then((res) => res.status >= 200 && res.status < 300)
      .catch(() => true)
      .finally(() => {
        sessionCheckInFlight = null
      })
  }
  return sessionCheckInFlight
}

/**
 * Configure axios interceptors for an axios instance
 */
export function configureInterceptors(axiosInstance: AxiosStatic | AxiosInstance) {

  axiosInstance.defaults.headers.Accept = 'application/json'
  axiosInstance.defaults.withCredentials = true

  axiosInstance.interceptors.request.use(
    (config: any) => {
      const { useAuthStore } = require('../../store/authStore')
      const authStore = useAuthStore.getState()

      // Read the CSRF token from cookies on every request. The token can rotate
      // (e.g. after login) without a page reload, so it must not be cached at
      // module load time.
      const cookies = new Cookies()
      const csrfToken = cookies.get('csrftoken')
      config.headers = {
        ...(config.headers || {}),
        'X-CSRFToken': csrfToken || 'LW3ZrdzJ0IANQw7pAomRjAyAbXNLY8dV',
      }

      if (config.url && config.url !== `${(window?.RUNTIME_CONFIG?.REACT_APP_API_ENDPOINT || process.env.REACT_APP_API_ENDPOINT)}/auth/users/me/` && authStore?.orgId) {
        config.headers = {
          ...(config.headers || {}),
          'X-Current-Organization': authStore.orgId
        }
      }
      config.validateStatus = function (status: number) {
        // Return true for successful status codes, false for errors
        // This determines whether to resolve or reject the promise
        return status >= 200 && status < 300
      }
      return config
    },
    (err: any) => Promise.reject(err)
  )

  axiosInstance.interceptors.response.use(
    (response: any) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

      // Handle 401 Unauthorized responses with token refresh logic. 401 is an
      // authentication failure by HTTP semantics, so no body inspection is
      // needed here. (A 403 can also mean "not authenticated" — that ambiguous
      // case is resolved by a session probe further below.)
      if (error.response?.status === 401 && originalRequest && !originalRequest.url?.includes('/auth/jwt/create/')) {


        const apiEndpoint = window?.RUNTIME_CONFIG?.REACT_APP_API_ENDPOINT || process.env.REACT_APP_API_ENDPOINT
        const refreshUrl = `${apiEndpoint}/auth/jwt/refresh/`

        // Skip refresh logic for the refresh and logout endpoints to prevent infinite loops
        if (originalRequest.url?.includes('/auth/jwt/refresh/') || originalRequest.url?.includes('/auth/jwt/logout/')) {
          // Don't attempt refresh for these endpoints
          if (originalRequest.url?.includes('/auth/jwt/refresh/')) {
            // Refresh itself failed - the session is dead, log out.
            await performLogout()
          }
          return Promise.reject(error)
        }

        // Already refreshed once and STILL unauthenticated → the refreshed
        // session is not usable, so log out instead of falling through to a
        // generic error toast.
        if (originalRequest._retry) {
          await performLogout()
          return Promise.reject(error)
        }

        // Mark request as retried to prevent infinite retry loops
        originalRequest._retry = true

        if (isRefreshing) {
          // A refresh is already in progress - queue this request
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({ resolve, reject })
          })
            .then(() => {
              // After refresh succeeds, retry the original request
              return axiosInstance(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        // Start the refresh process
        isRefreshing = true

        try {
          // Attempt to refresh the token
          // withCredentials is needed to send the refresh token cookie
          await axiosInstance.post(refreshUrl, {}, { withCredentials: true })

          // Refresh succeeded - process all queued requests
          failedRequestsQueue.forEach((req) => req.resolve())
          failedRequestsQueue = []

          // Retry the original request with the new session
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          // Refresh failed - reject all queued requests
          failedRequestsQueue.forEach((req) => req.reject(refreshError))
          failedRequestsQueue = []

          // Logout - dispatch event for React Router to handle
          await performLogout()

          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      // A 403 is ambiguous: it can mean "not authenticated" (Django REST
      // Framework serializes NotAuthenticated as 403 under session auth) or a
      // genuine "permission denied". Probe the session to tell them apart using
      // a stable HTTP signal instead of parsing localized error text. If the
      // session is dead, log out; otherwise fall through to the existing
      // forbidden handling (toast + redirect). This is what makes an expired
      // session on a 403-returning endpoint actually log the user out.
      if (error.response?.status === 403 && originalRequest && !(await isSessionValid())) {
        await performLogout()
        return Promise.reject(error)
      }

      // A flagged request's component-access refusal is rendered by its caller:
      // no toast, and no forbidden redirect.
      if (isOwnedComponentRefusal(error.response?.status, error.response?.data, originalRequest)) {
        return Promise.reject(error)
      }

      // A refused run start (`WORKFLOW_NOT_RUNNABLE`) is listed per step by the
      // caller's modal; no toast on top.
      if (isOwnedNotRunnable(error.response?.status, error.response?.data)) {
        return Promise.reject(error)
      }

      // Handle error display for the remaining errors (401s and authentication
      // 403s are handled above and have already returned). Don't show error
      // toast for logout and refresh endpoints.
      if (isShouldErrorHandling(originalRequest?.url) && !originalRequest?._ownsErrors) {
        if (error.response?.status && error.response?.status >= 500) {
          toastErrorDeduped(() => (<><strong>Oops! Something went wrong.</strong><p>We're working on it. Please try again shortly.</p></>), { key: 'http-5xx' })
        } else {
          switch (error.response?.status) {
            case 500:
              toastErrorDeduped(() => (<><strong>Oops! Something went wrong.</strong><p>We're working on it. Please try again shortly.</p></>), { key: 'http-5xx' })
              break;
            case 502:
              toastErrorDeduped(() => (<><strong>Oops! Something went wrong.</strong><p>We're working on it. Please try again shortly.</p></>), { key: 'http-5xx' })
              break;
            // case 403:
            //   break;
            case 429:
              toastErrorDeduped(() => (<><strong>Oops! The service is temporarily unavailable.</strong><p>We'll be back shortly.</p></>), { key: 'http-429' })
              break;
            default:
              if (error.response) {
                if (error.response.data) {
                  if (typeof error.response.data === 'object') {
                    if (!Array.isArray(error.response.data)) {
                      let errorText = ``
                      Object.entries(error.response.data).forEach(([field, detail]) => {
                        const line = formatErrorDetail(detail)
                        if (line) errorText = `${errorText} \n  ${line}`
                      })
                      toastErrorDeduped(errorText)
                    } else {
                      toastErrorDeduped(error.response.data[0] as string)
                    }
                  } else {
                    toastErrorDeduped(error.response.data as string)
                  }
                }
              } else if (error.request) {
                toastErrorDeduped('Network Error: No response received')
              } else {
                toastErrorDeduped(`General Error: ${error.message}`)
              }

          }
        }
      }
      // Handle 403 Forbidden - redirect to org dashboard, unless the surface
      // renders its own 403 in place (see `ignoreForbiddenRedirectList`).
      if (
        error.response?.status === 403 &&
        !originalRequest.url?.includes("organizations") &&
        isShouldForbiddenRedirect(originalRequest.url) &&
        !originalRequest._ownsErrors
      ) {
        // toast.error('Access forbidden')
        // Close all open Bootstrap modals before redirecting to avoid a frozen page
        import('bootstrap').then(({ Modal }) => {
          document.querySelectorAll('.modal.show').forEach((el) => {
            Modal.getInstance(el)?.hide()
          })
        })
        window.dispatchEvent(new CustomEvent('auth:forbidden'))
        return Promise.reject(error)
      }
      return Promise.reject(error)
    }
  )
}

/**
 * Setup axios interceptors for both global axios and apiClient
 */
export default function setupAxios(axios: AxiosStatic) {
  // Configure the global axios instance
  configureInterceptors(axios)

  // Configure the apiClient instance (this is what's actually used for API calls)
  configureInterceptors(apiClient)
}
